import { useState, useEffect, useCallback } from 'react';
import { API, useAuth } from '@/App';
import axios from 'axios';

// localStorage cache so the second+ refresh of any dashboard renders the
// gated sidebar INSTANTLY instead of showing nothing for a few seconds
// while /api/my-features finishes. We still fire the request in the
// background so any admin-side change to the user's role features is
// reflected on the next render — see Feb 2026 fix for "empty sidebar
// for 5s" bug. Falls back gracefully if storage is unavailable.
const CACHE_KEY_PREFIX = 'unexa:role_features:';
const CACHE_VERSION = 'v1';

// Read user_id from the JWT in localStorage WITHOUT waiting for the
// auth context — lets the very first render of any dashboard hit the
// cache synchronously.
function userIdFromToken() {
  try {
    const t = localStorage.getItem('token');
    if (!t) return null;
    const parts = t.split('.');
    if (parts.length < 2) return null;
    // JWT base64url payload — pad and decode.
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const pad = b64.length % 4 ? '='.repeat(4 - (b64.length % 4)) : '';
    const json = atob(b64 + pad);
    const payload = JSON.parse(json);
    return payload.user_id || payload.sub || null;
  } catch {
    return null;
  }
}

function readCache(userId) {
  if (!userId) return null;
  try {
    const raw = localStorage.getItem(`${CACHE_KEY_PREFIX}${userId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.v !== CACHE_VERSION) return null;
    return parsed.data || null;
  } catch {
    return null;
  }
}

function writeCache(userId, data) {
  if (!userId) return;
  try {
    localStorage.setItem(
      `${CACHE_KEY_PREFIX}${userId}`,
      JSON.stringify({ v: CACHE_VERSION, ts: Date.now(), data })
    );
  } catch {
    /* storage full / private mode — ignore */
  }
}

export function useFeatureFlags() {
  const { user } = useAuth();
  // Seed state synchronously from cache so the very first render is
  // already filtered. Avoids the "empty sidebar then pop-in" UX.
  // user.user_id may be undefined on first render — fall back to the
  // JWT payload (decoded from localStorage) so cache hits work the
  // moment React mounts.
  const initial = (() => {
    const uid = (user?.user_id || user?.id) || userIdFromToken();
    const c = uid ? readCache(uid) : null;
    return {
      role: c?.roleFeatures || {},
      social: c?.socialFeatures || {},
      loaded: !!c, // treat cache as loaded so filterNav applies the filter
    };
  })();
  const [roleFeatures, setRoleFeatures] = useState(initial.role);
  const [socialFeatures, setSocialFeatures] = useState(initial.social);
  const [loaded, setLoaded] = useState(initial.loaded);

  useEffect(() => {
    if (!user) return;
    const uid = user.user_id || user.id;
    // Cache hydration already happened in the useState initializer via
    // userIdFromToken(). The effect just refreshes from the network.
    let cancelled = false;
    axios.get(`${API}/my-features`)
      .then(res => {
        if (cancelled) return;
        const role = res.data.role_features || {};
        const social = res.data.social_features || {};
        setRoleFeatures(role);
        setSocialFeatures(social);
        writeCache(uid, { roleFeatures: role, socialFeatures: social });
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, [user]);

  const isAllowed = useCallback((key) => {
    if (!loaded) return true;
    if (key in roleFeatures) return roleFeatures[key];
    if (key in socialFeatures) return socialFeatures[key];
    return true;
  }, [loaded, roleFeatures, socialFeatures]);

  const filterNav = useCallback((items, keyField = 'flagKey') => {
    return items.filter(item => {
      const fk = item[keyField];
      if (!fk) return true; // No gate — always visible (Logout, etc.)
      // While flags are still loading (no cache for this user yet —
      // common when switching accounts) HIDE the flagged item rather
      // than leak it. The hooks hydrate from localStorage cache on the
      // initial render, so this only ever bites on a truly fresh login.
      // Once /api/my-features lands, the component re-renders with the
      // real value. Feb 2026 fix.
      if (!loaded) return false;
      return isAllowed(fk);
    });
  }, [loaded, isAllowed]);

  return { roleFeatures, socialFeatures, loaded, isAllowed, filterNav };
}
